import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

interface PixelPortraitProps {
    src: string
    alt: string
    sizes: string
    className?: string
    delay?: number
}

const LOAD_STEPS = [28, 20, 14, 10, 7, 5, 3, 2]

const GLITCH_STEPS = [12, 7, 4, 2]

const STEP_MS = 90

/**
 * A photo that "loads in" like an old game: it starts as big pixel blocks and sharpens
 * once it scrolls into view, and glitches back for a moment when hovered.
 * The real image is always in the markup with its alt text; the canvas is decoration.
 */
const PixelPortrait: FC<PixelPortraitProps> = ({
    src,
    alt,
    sizes,
    className,
    delay = 0
}): ReactNode => {
    const [wrapperRef, inView] = useInView({ threshold: 0.35 })
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const imageRef = useRef<HTMLImageElement>(null)
    const bufferRef = useRef<HTMLCanvasElement | null>(null)
    const timersRef = useRef<Array<number>>([])
    const busyRef = useRef(false)

    const [enhanced, setEnhanced] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [sharp, setSharp] = useState(false)

    const clearTimers = useCallback(() => {
        timersRef.current.forEach(timer => window.clearTimeout(timer))
        timersRef.current = []
    }, [])

    const draw = useCallback((block: number) => {
        const canvas = canvasRef.current
        const image = imageRef.current

        if (!canvas || !image || !image.naturalWidth) return

        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        const width = Math.max(1, Math.round(canvas.clientWidth * ratio))
        const height = Math.max(1, Math.round(canvas.clientHeight * ratio))

        if (canvas.width !== width) canvas.width = width
        if (canvas.height !== height) canvas.height = height

        const context = canvas.getContext('2d')

        if (!context) return

        const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight)
        const sourceWidth = width / scale
        const sourceHeight = height / scale
        const sourceX = (image.naturalWidth - sourceWidth) * 0.5
        const sourceY = (image.naturalHeight - sourceHeight) * 0.25
        const cell = Math.max(1, Math.round(block * ratio))
        const smallWidth = Math.max(1, Math.ceil(width / cell))
        const smallHeight = Math.max(1, Math.ceil(height / cell))

        const buffer = bufferRef.current ?? document.createElement('canvas')

        bufferRef.current = buffer
        buffer.width = smallWidth
        buffer.height = smallHeight

        const bufferContext = buffer.getContext('2d')

        if (!bufferContext) return

        bufferContext.imageSmoothingEnabled = true
        bufferContext.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, smallWidth, smallHeight)

        context.imageSmoothingEnabled = false
        context.clearRect(0, 0, width, height)
        context.drawImage(buffer, 0, 0, smallWidth, smallHeight, 0, 0, smallWidth * cell, smallHeight * cell)
    }, [])

    const play = useCallback((steps: ReadonlyArray<number>, startDelay: number) => {
        clearTimers()
        busyRef.current = true

        steps.forEach((block, index) => {
            timersRef.current.push(window.setTimeout(() => draw(block), startDelay + index * STEP_MS))
        })

        timersRef.current.push(window.setTimeout(() => {
            busyRef.current = false
            setSharp(true)
        }, startDelay + steps.length * STEP_MS))
    }, [clearTimers, draw])

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setSharp(true)
            return
        }

        setEnhanced(true)
    }, [])

    useEffect(() => clearTimers, [clearTimers])

    useEffect(() => {
        if (enhanced && loaded && !sharp && !busyRef.current) draw(LOAD_STEPS[0])
    }, [enhanced, loaded, sharp, draw])

    useEffect(() => {
        if (!enhanced || !loaded || !inView || sharp || busyRef.current) return

        play(LOAD_STEPS, delay)
    }, [enhanced, loaded, inView, sharp, delay, play])

    const glitch = () => {
        if (!enhanced || !loaded || !sharp || busyRef.current) return

        draw(GLITCH_STEPS[0])
        setSharp(false)
        play(GLITCH_STEPS, 0)
    }

    const showCanvas = enhanced && !sharp

    return (
        <div
            ref={wrapperRef}
            onPointerEnter={glitch}
            className={cn('relative overflow-hidden bg-paper', className)}
        >
            <Image
                ref={imageRef}
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                onLoad={() => setLoaded(true)}
                onError={() => setSharp(true)}
                className={cn('object-cover object-[50%_25%]', showCanvas && 'opacity-0')}
            />

            <canvas
                ref={canvasRef}
                aria-hidden='true'
                className={cn('absolute inset-0 size-full', !showCanvas && 'opacity-0')}
            />
        </div>
    )
}

export default PixelPortrait
